/**
 * @wl/widget-mortgage — тексты блока «Семейная ипотека».
 *
 * Чистые функции «числа → строка»: index.ts только кладёт результат в DOM, а
 * формулировки проверяются тестами. Это официальная программа, и каждая строка
 * здесь — обещание покупателю: правка текста без правки теста не проходит.
 *
 * Типографика: неразрывный пробел перед «₽», «%» и «млн», чтобы число не
 * отрывалось от единицы на границе строки (на 375 px это случается).
 */

import {
  FAMILY_PROGRAM,
  familyQuote,
  type FamilyQuote,
  type FamilyTerms,
  type FamilyUnavailable,
} from '@wl/shared-ui/family-mortgage';
import { formatRub } from './calculator';
import { formatNum } from './format';
import type { FamilyOfferDetails } from './offers';

const NBSP = ' ';

/** «10 %» с неразрывным пробелом. */
export function pctText(value: number, maxFrac = 2): string {
  return `${formatNum(value, maxFrac)}${NBSP}%`;
}

/** «39 701 ₽» — рубли, целые. */
export function rubText(value: number): string {
  return `${formatRub(value)}${NBSP}₽`;
}

/** «6 млн ₽» · «1,71 млн ₽»: до сотых миллиона, хвостовые нули отброшены. */
export function mlnText(value: number): string {
  return `${formatNum(value / 1_000_000, 2)}${NBSP}млн${NBSP}₽`;
}

/** Памятка о том, кому программа нужна: показывается мелко под ставкой. */
export const FAMILY_ELIGIBILITY_NOTE = 'Программа для семей, где хотя бы одному ребёнку меньше 7 лет';

/**
 * Строка-пояснение под выбором числа детей: какая ставка и до какой суммы она
 * действует. Пересчитывается при каждом изменении детей, инвалидности и взноса.
 *
 * `tip` — вторая мелкая строка «При взносе от 50 % — ставка 6 %»: показывается
 * всегда, пока ярусная ставка выше льготной и льгота ещё не применена (ни взноса
 * от 50 %, ни ребёнка-инвалида). Покупатель с 10 % узнаёт, чем её можно снизить.
 * `null` — подсказывать нечего.
 */
export function familyHintText(
  quote: FamilyQuote,
  terms: FamilyTerms,
): { main: string; tip: string | null; note: string } {
  const limit = `льготная сумма до${NBSP}${mlnText(quote.maxLoan)}`;
  let lead: string;
  if (quote.reducedBy === 'down') {
    lead = `Взнос от${NBSP}${pctText(terms.reducedDownPct)} — ставка ${pctText(quote.ratePct)}`;
  } else if (quote.reducedBy === 'disabled') {
    lead = `Ребёнок с инвалидностью — ставка ${pctText(quote.ratePct)}`;
  } else {
    lead = `Ставка ${pctText(quote.ratePct)}`;
  }
  const tip =
    quote.reducedBy === null && quote.tierRatePct > terms.reducedRatePct
      ? `При взносе от${NBSP}${pctText(terms.reducedDownPct)} — ставка ${pctText(terms.reducedRatePct)}`
      : null;
  return { main: `${lead} · ${limit}`, tip, note: FAMILY_ELIGIBILITY_NOTE };
}

/** Подписи кнопок числа детей для скринридера: «1 ребёнок», «2 ребёнка», «5 и более детей». */
export const CHILDREN_LABELS = ['1 ребёнок', '2 ребёнка', '3 ребёнка', '4 ребёнка', '5 и более детей'] as const;

/**
 * Ставка яруса на каждой кнопке «Детей в семье»: та же `familyQuote`, что считает
 * платёж, — для текущего взноса и галочки инвалидности. При взносе от 50 % на
 * кнопках 1 и 2 ставка 6 %: покупатель видит, что вторая ступень уже сработала.
 */
export function familyTierRates(
  terms: FamilyTerms,
  dpPct: number,
  disabledChild: boolean,
): Array<{ children: number; ratePct: number; label: string }> {
  const out: Array<{ children: number; ratePct: number; label: string }> = [];
  for (let n = FAMILY_PROGRAM.minChildren; n <= FAMILY_PROGRAM.maxChildren; n += 1) {
    const q = familyQuote(terms, { children: n, downPct: dpPct, disabledChild });
    out.push({
      children: n,
      ratePct: q.ratePct,
      label: `${CHILDREN_LABELS[n - 1] ?? String(n)}, ставка ${pctText(q.ratePct)}`,
    });
  }
  return out;
}

/** «год / года / лет» по числу. */
export function yearsWord(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'год';
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'года';
  return 'лет';
}

/**
 * «С 16-го года — до 39 972 ₽/мес: льготная ставка действует 15 лет, дальше — до
 * 14 %». null — платёж не меняется (срок укладывается в льготные годы).
 *
 * Объясняет ПРИЧИНУ роста, а не только число: платёж, прыгнувший на 7 тысяч без
 * слова, читался как подвох. Срок субсидии — из условий программы, не из головы.
 */
export function familyAfterText(pay: FamilyOfferDetails, terms: FamilyTerms): string | null {
  if (pay.monthlyAfter === null) return null;
  const years = terms.subsidyYears;
  const rate = pay.rateAfterPct === null ? '' : `, дальше${NBSP}— до${NBSP}${pctText(pay.rateAfterPct)}`;
  return (
    `С ${years + 1}-го года — до${NBSP}${formatRub(pay.monthlyAfter)}${NBSP}₽/мес: ` +
    `льготная ставка действует ${years}${NBSP}${yearsWord(years)}${rate}`
  );
}

/**
 * «6 млн ₽ под 10 % + 1,71 млн ₽ под 17,7 % — сумма сверх лимита программы идёт
 * по рыночной ставке». null — вся сумма в лимите.
 */
export function familyComboText(
  pay: FamilyOfferDetails,
  prefRatePct: number,
  marketRatePct: number | undefined,
): string | null {
  if (pay.marketLoan <= 0) return null;
  const market = typeof marketRatePct === 'number' ? ` под ${pctText(marketRatePct)}` : '';
  return (
    `${mlnText(pay.prefLoan)} под ${pctText(prefRatePct)} + ${mlnText(pay.marketLoan)}${market}` +
    ' — сумма сверх лимита программы идёт по рыночной ставке'
  );
}

/** Что показать вместо пустой выдачи: заголовок с причиной и пояснение числами. */
export interface UnavailableView {
  title: string;
  text: string;
}

/**
 * Причина, по которой по «Семейной» не осталось ни одного банка. Вместо пустого
 * экрана — что случилось, а что сделать, говорят кнопки под текстом: «Увеличить
 * взнос до N ₽» и «Посмотреть другую программу».
 *
 * @param principal сумма кредита сейчас, ₽ — чтобы показать её рядом с пределом
 */
export function familyUnavailableView(
  reason: FamilyUnavailable,
  loanCap: number,
  principal: number,
): UnavailableView {
  if (reason === 'over_combined_limit') {
    return {
      title: 'Кредит выше предела Семейной ипотеки',
      text:
        `По программе можно взять не больше${NBSP}${mlnText(loanCap)}, ` +
        `а ваш кредит —${NBSP}${mlnText(principal)}.`,
    };
  }
  return {
    title: 'Сумму сверх лимита банки не выдают',
    text:
      `Льготная часть кредита по Семейной ипотеке — не больше${NBSP}${mlnText(loanCap)}, ` +
      'а на остальное у банков нет рыночной ставки.',
  };
}

/** Заголовок пустой выдачи, когда причина не в сумме (банки не дают программу). */
export const NO_OFFERS_TITLE = 'По этой программе предложений нет';

/** Текст кнопки «Увеличить взнос до 5 000 000 ₽». */
export function raiseDownLabel(dpAmount: number): string {
  return `Увеличить взнос до${NBSP}${rubText(dpAmount)}`;
}

/** Текст кнопки перехода на другую программу. */
export const OTHER_PROGRAM_LABEL = 'Посмотреть другую программу';
